import { createContext, isValidElement, useContext, useMemo, type ComponentProps, type ReactNode } from 'react';
import { useChatExperience } from '../chat-experience';
import {
  formatSourceLocation,
  getDocumentDisplayName,
  getSourceLocation,
  type DocumentCitation,
} from '../chat-document-sources';
import { getWebSourceSiteName, type WebCitationSource } from '../chat-web-citations';

type CitationSources = {
  documents: ReadonlyMap<string, DocumentCitation>;
  web: readonly WebCitationSource[];
};

const CitationSourcesContext = createContext<CitationSources>({ documents: new Map(), web: [] });

export function CitationSourcesProvider({
  children,
  documentCitations,
  webSources,
}: {
  children: ReactNode;
  documentCitations: readonly DocumentCitation[];
  webSources: readonly WebCitationSource[];
}) {
  const value = useMemo<CitationSources>(() => ({
    documents: new Map(documentCitations.map((citation) => [citation.citationKey, citation])),
    web: webSources,
  }), [documentCitations, webSources]);
  return <CitationSourcesContext.Provider value={value}>{children}</CitationSourcesContext.Provider>;
}

type MarkdownCitationProps = ComponentProps<'cite'> & { node?: unknown };

const BADGE_CLASS_NAME = 'not-prose ml-1.5 inline-flex max-w-56 items-center gap-1 rounded-full bg-muted px-2 py-0.5 align-[0.1em] text-[0.7rem] font-medium leading-4 text-muted-foreground no-underline transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

// While Streamdown animates a block, text children arrive wrapped in spans.
function getChildText(children: ReactNode): string {
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(getChildText).join('');
  if (isValidElement<{ children?: ReactNode }>(children)) return getChildText(children.props.children);
  return '';
}

function pluralizeOthers(count: number, noun: string) {
  return `et ${count} autre${count > 1 ? 's' : ''} ${noun}${count > 1 ? 's' : ''} cité${count > 1 ? 's' : ''}`;
}

/**
 * Rounded site badge placed at the end of a cited block. It opens the first
 * cited source directly; "+N" counts the other cited sites of the block.
 */
function WebCitationBadge({ keys, title }: { keys: string[]; title?: string }) {
  const { web } = useContext(CitationSourcesContext);
  const cited = keys.flatMap((key) => web.find((source) => source.citationKey === key) ?? []);
  const primary = cited[0];
  if (!primary || primary.url !== title) {
    return null;
  }

  const siteName = getWebSourceSiteName(primary.url);
  const otherSiteCount = new Set(
    cited.map((source) => getWebSourceSiteName(source.url)).filter((name) => name !== siteName),
  ).size;
  const label = otherSiteCount > 0
    ? `Ouvrir ${siteName} (${pluralizeOthers(otherSiteCount, 'site')}) dans un nouvel onglet`
    : `Ouvrir ${siteName} dans un nouvel onglet`;

  return (
    <a
      aria-label={label}
      className={BADGE_CLASS_NAME}
      href={primary.url}
      rel="noreferrer"
      target="_blank"
    >
      <span className="truncate">{siteName}</span>
      {otherSiteCount > 0 ? <span aria-hidden="true">+{otherSiteCount}</span> : null}
    </a>
  );
}

/**
 * Same rounded badge for company documents. It opens the in-app document
 * viewer on the first cited passage, highlighted, with the other passages of
 * that document cited by the block; "+N" counts the other cited documents.
 */
function DocumentCitationBadge({ keys }: { keys: string[] }) {
  const { documents } = useContext(CitationSourcesContext);
  const { openDocument } = useChatExperience();
  const cited = keys.flatMap((key) => documents.get(key) ?? []);
  const primary = cited[0];
  if (!primary) {
    return null;
  }

  const name = getDocumentDisplayName(primary.document);
  const location = getSourceLocation(primary.reference);
  const otherDocumentCount = new Set(
    cited.map((citation) => citation.documentKey).filter((documentKey) => documentKey !== primary.documentKey),
  ).size;
  const primaryReferences = cited
    .filter((citation) => citation.documentKey === primary.documentKey)
    .flatMap((citation) => (citation.reference ? [citation.reference] : []));
  const label = [
    `Ouvrir ${name}`,
    location ? `, ${formatSourceLocation(location, 'long')}` : '',
    otherDocumentCount > 0 ? ` (${pluralizeOthers(otherDocumentCount, 'document')})` : '',
  ].join('');

  // No hover preview (product decision 2026-10-07): the badge is the action, a native button
  // opened by click, Enter or Space.
  return (
    <button
      aria-label={label}
      className={`${BADGE_CLASS_NAME} cursor-pointer`}
      data-testid="document-citation-badge"
      onClick={() => openDocument(primary.document, primary.reference, primaryReferences)}
      type="button"
    >
      <span className="truncate">{name}</span>
      {location ? <span className="shrink-0">· {formatSourceLocation(location, 'short')}</span> : null}
      {otherDocumentCount > 0 ? <span aria-hidden="true" className="shrink-0">+{otherDocumentCount}</span> : null}
    </button>
  );
}

/** Markdown `cite` renderer produced by `remarkCitationBadges`. */
export function CitationBadge({ title, children }: MarkdownCitationProps) {
  const keys = getChildText(children).split(/\s+/).filter(Boolean);
  if (keys.length === 0) {
    return null;
  }
  return keys[0]!.startsWith('D')
    ? <DocumentCitationBadge keys={keys} />
    : <WebCitationBadge keys={keys} title={title} />;
}
